/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { UiService } from '@services/ui/ui.service';
import { Injectable } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { LabelsService } from '@services/labels/labels.service';
import { Network } from '@capacitor/network';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  /**
   * stato di connessione
   */
  private connected = true;

  /**
   * elementi UI per gestire gli stati della connessione
   */
  private connectionElements: {
    alert?: HTMLIonAlertElement;
    toast?: HTMLIonToastElement;
  } = {};

  constructor(
    private uiService: UiService,
    private labelsService: LabelsService,
    private alertController: AlertController,
    private toastController: ToastController
  ) {}

  /**
   * controlla la compatibilità del browser e mostra avviso
   */
  async checkDeviceCompatibility(): Promise<void> {
    if (this.isIosChrome()) {
      await this.uiService.showAlert(
        'wrn.device_compatibility_title',
        'wrn.device_compatibility_ios_chrome_message'
      );
    }
  }

  /**
   * controlla che  il browser sia chrome su ios
   */
  isIosChrome(): boolean {
    return navigator.userAgent.match('CriOS') !== null;
  }

  /**
   * avvia ascolto dello stato della connessione e mostra avvisi
   */
  async listenConnectionLoss(): Promise<void> {
    // creo subito gli elementi in modo che i moduli ionic
    // necessari vengano caricati subito e siano disponibile quando si perde la connessione
    await this.createConnectionElements();

    Network.addListener('networkStatusChange', status => {
      this.updateConnected(status.connected);
    });
  }

  /**
   * crea gli elementi da utilizzare per mostrare i cambi di stato della connessione
   */
  private async createConnectionElements(): Promise<void> {
    this.connectionElements.alert = await this.alertController.create({
      backdropDismiss: false,
      cssClass: 'connection-alert',
      header: this.labelsService.getLabel('msg.connection_off_title'),
      message: this.labelsService.getLabel('msg.connection_off_message')
    });

    this.connectionElements.toast = await this.toastController.create({
      message: this.labelsService.getLabel('msg.connection_on_title'),
      duration: 2000,
      position: 'top',
      color: 'success'
    });
  }

  /**
   * aggiorna lo stato di connessione e mostra i relativi warning
   */
  private async updateConnected(value: boolean): Promise<void> {
    const prevConnected = this.connected;

    this.connected = value;

    if (prevConnected && !this.connected) {
      await this.wentOffline();
    } else if (!prevConnected && this.connected) {
      await this.wentOnline();
    }
  }

  private async wentOffline(): Promise<void> {
    this.updateConnectionLabels();
    await this.connectionElements.alert.present();
  }

  private async wentOnline(): Promise<void> {
    this.updateConnectionLabels();
    await this.connectionElements.alert.dismiss();
    await this.connectionElements.toast.present();
  }

  private updateConnectionLabels(): void {
    this.connectionElements.alert.header = this.labelsService.getLabel(
      'msg.connection_off_title'
    );
    this.connectionElements.alert.message = this.labelsService.getLabel(
      'msg.connection_off_message'
    );
    this.connectionElements.toast.message = this.labelsService.getLabel(
      'msg.connection_on_title'
    );
  }
}
